import { CardRow } from 'components/CardRow';
import { ItemDetails } from 'components/ItemDetails';
import { ModalScreens } from 'components/Navigation/types';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { Txt } from 'components/Txt';
import equal from 'fast-deep-equal';
import { useSelector } from 'hooks/useSelector';
import { getOptionFieldResultDisplayValues, hasValue } from 'lib/fields';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import { InstanceParentParams } from 'store/assessments/types';
import { selectAssetFieldRepeatableInstance } from 'store/assets/selectors';
import { FieldTypes } from 'store/assets/types';
import { FieldGroupWithCalculatedInfo } from 'store/organisations/types';
import { UUID } from 'types/types';

type Props = {
  assetId: UUID;
  instanceId: string;
  group: FieldGroupWithCalculatedInfo;
  parent: NonNullable<InstanceParentParams>;
};

export const LightRepeatableListItem: React.FC<Props> = React.memo(
  ({ assetId, instanceId, group, parent }) => {
    const dispatch = useDispatch();

    const instance = useSelector(
      (state) => selectAssetFieldRepeatableInstance(state, assetId, instanceId),
      equal,
    );

    if (!instance || !group) {
      return null;
    }

    const { imageFieldId } = group.calculatedInfo;

    const imageId: string | undefined = imageFieldId
      ? instance.results[imageFieldId]?.value?.[0]
      : undefined;

    const resultsCount = Object.values(instance.results).filter(
      hasValue,
    ).length;

    const additionalTitles = group.fieldIds.reduce<string[]>((acc, fieldId) => {
      // Field must be included in fieldsConfig for the user to see it in this assessment
      const field = group.fields[fieldId]!;
      const result = instance.results[fieldId];
      if (result) {
        if (field.type === FieldTypes.OPTION) {
          const optionValues = getOptionFieldResultDisplayValues(
            field,
            result,
            true,
          ).join(', ');
          if (optionValues && optionValues !== 'Yes' && optionValues !== 'No') {
            acc.push(optionValues);
          }
        } else if (
          field.type === FieldTypes.TEXT ||
          field.type === FieldTypes.NOTE
        ) {
          if (result.value?.length) {
            acc.push(result.value as string);
          }
        }
      }
      return acc;
    }, []);

    const handlePress = () =>
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.AstLightRepeatableInstance,
          params: {
            assetId,
            groupId: group.id,
            instanceId,
            parent,
          },
        }),
      );

    return (
      <CardRow onPress={handlePress}>
        <View
          style={{
            alignItems: 'center',
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          {!!imageFieldId && (
            <View style={{ marginRight: theme.padding, width: 52 }}>
              <LocalThumbnail
                width={52}
                height={52}
                rounded
                thumbnailId={imageId}
              />
            </View>
          )}
          <ItemDetails
            titleStyledAsCardLabel
            title={additionalTitles.join(' • ') || 'No Value'}
            subtitle={
              <Txt size="xsmall">
                {pluralise(
                  resultsCount,
                  '1 Result',
                  '{x} Results',
                  'No Results',
                )}
              </Txt>
            }
          />
        </View>
      </CardRow>
    );
  },
);
